/**
 * The project page, in SECTIONS.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The page used to render twenty-three registers down one scroll, each with the
 * same weight and nothing saying which of them wanted anything. The user's
 * words on first testing it: *"its bit messy and no body will understand it"*,
 * *"nobody will scroll like this"*. The information was all there — pressing
 * "Complete Award" listed exactly what the stage was missing — but nothing on
 * the page pointed at it.
 *
 * So the registers are grouped into seven sections, one is open at a time, and
 * the section is in the address bar (`/projects/<id>/site`). Two consequences
 * worth keeping:
 *
 *   * a link to a register is a link somebody can send, and it opens where it
 *     says it does after a refresh;
 *   * a register that is not on screen is not fetched, so opening a project
 *     costs the overview's queries rather than all twenty-three.
 *
 * THE ORDER IS THE ORDER THE WORK HAPPENS IN, not alphabetical and not the
 * order the modules were built in. Design, then approvals and procurement, then
 * site, then quality, then handover — the same spine as the eight lifecycle
 * stages, so somebody who knows the job can guess where a thing lives.
 */

/** The tabs. `overview` is the default and must stay first. */
export const SECTION_KEYS = [
  'overview',
  'design',
  'procurement',
  'site',
  'quality',
  'commercial',
  'records',
] as const;

export type SectionKey = (typeof SECTION_KEYS)[number];

export interface SectionDef {
  readonly key: SectionKey;
  /** The tab's own word. Short: seven of these share one row on a laptop. */
  readonly label: string;
  /** What is in here, for the tab's title attribute and the walkthrough. */
  readonly holds: string;
}

export const SECTIONS: readonly SectionDef[] = [
  { key: 'overview', label: 'Overview', holds: 'Pulse, lifecycle, details, team, milestones' },
  { key: 'design', label: 'Design', holds: 'Drawings and submittals' },
  { key: 'procurement', label: 'Procurement', holds: 'Materials, RFQs, orders and deliveries' },
  { key: 'site', label: 'Site', holds: 'Diary, activities, tasks and photos' },
  { key: 'quality', label: 'Quality', holds: 'Inspections, NCRs, snags and handover' },
  { key: 'commercial', label: 'Commercial', holds: 'Budget, costs, variations and invoices' },
  {
    key: 'records',
    label: 'Records',
    holds: 'Documents, reports, timeline, locations and work packages',
  },
];

const KEYS: ReadonlySet<string> = new Set(SECTION_KEYS);

/**
 * The section a URL segment names, falling back to the overview.
 *
 * A typo in a pasted link opens the overview rather than an error page: the
 * project is what the reader asked for, and the section is a detail of where
 * they land inside it.
 */
export function sectionFrom(segment: string | null): SectionKey {
  return segment && KEYS.has(segment) ? (segment as SectionKey) : 'overview';
}

/**
 * A link to a section of a project, optionally pointing at one record in it.
 *
 * `focus` is how the cross-links work: the target register scrolls that record
 * into view and marks it, so "the submittal this material is waiting on" lands
 * on the submittal rather than on the register that contains it somewhere.
 */
export function sectionHref(projectId: string, section: SectionKey, focus?: string): string {
  const base = `/projects/${encodeURIComponent(projectId)}/${section}`;
  return focus ? `${base}?focus=${encodeURIComponent(focus)}` : base;
}

// ---------------------------------------------------------------------------
// Records that have a page of their own
// ---------------------------------------------------------------------------

/**
 * A register whose records each have an address.
 *
 * WHICH RECORDS EARN A PAGE, AND WHICH DO NOT
 * -------------------------------------------
 * The same question that decided drawer-versus-inline decides this, one level
 * up: **a page is for a record that has a life of its own.** A material has a
 * procurement history, an approval it waited on and a file trail — three things
 * that are each a list, and a drawer 480px wide showing three lists is a drawer
 * nobody can read. A location is a name and a parent; it has no history, and a
 * page for it would be a page with one field on it.
 *
 * So this map is a decision, not a lookup table to be completed. A register
 * missing from it is a register whose records are still a drawer ON PURPOSE,
 * and `HANDOVER-2026-REWORK.md` §4.3 says which ones and why.
 */
export interface RecordRegister {
  /** Where the register lives, so the tab strip stays right on a record page. */
  readonly section: SectionKey;
  /** The register's own name, for the link back to it. A heading. */
  readonly label: string;
  /** One record, for the sentence shown when an id names nothing. */
  readonly noun: string;
  /**
   * Many of them, cased for the MIDDLE of a sentence — "Open the materials".
   *
   * Spelled out rather than lower-casing `label`, because that turns "NCRs" into
   * "ncrs". An acronym is not a word with a capital letter on it.
   */
  readonly plural: string;
}

export const RECORD_PAGES: Readonly<Record<string, RecordRegister>> = {
  materials: {
    section: 'procurement',
    label: 'Materials',
    noun: 'material',
    plural: 'materials',
  },
  drawings: { section: 'design', label: 'Drawings', noun: 'drawing', plural: 'drawings' },
  submittals: { section: 'design', label: 'Submittals', noun: 'submittal', plural: 'submittals' },
  snags: { section: 'quality', label: 'Snags', noun: 'snag', plural: 'snags' },
  tasks: { section: 'site', label: 'Tasks', noun: 'task', plural: 'tasks' },
  activities: { section: 'site', label: 'Activities', noun: 'activity', plural: 'activities' },
  variations: {
    section: 'commercial',
    label: 'Variations',
    noun: 'variation',
    plural: 'variations',
  },
  /*
   * TWO REGISTERS IN ONE SECTION, AND ONE SCREEN RENDERS BOTH. This is the case
   * the four-segment address exists for: `/projects/<id>/quality/<uuid>` cannot
   * say whether the id is an inspection or an NCR, and `Quality.tsx` takes the
   * register alongside the id for exactly that reason.
   */
  inspections: {
    section: 'quality',
    label: 'Inspections',
    noun: 'inspection',
    plural: 'inspections',
  },
  ncrs: { section: 'quality', label: 'NCRs', noun: 'NCR', plural: 'NCRs' },
};

/** The register a record path names, or null if it does not name a real one. */
export function recordRegisterFrom(
  section: SectionKey,
  register: string | null,
): RecordRegister | null {
  if (!register) return null;
  const found = RECORD_PAGES[register];
  // The section has to agree. A register lives in exactly one section, so a URL
  // whose two segments disagree was typed or truncated rather than followed —
  // and the graceful answer is the same one `sectionFrom` gives a typo: show the
  // section that was asked for, rather than an error page.
  return found && found.section === section ? found : null;
}

/**
 * A record's own address: `/projects/<id>/<section>/<register>/<record>`.
 *
 * The register is IN the address rather than implied by the section, because a
 * section holds more than one register and an id on its own cannot say which.
 * It also means the page's back link is this address with the last two segments
 * removed, which is `sectionHref` — derived, never remembered.
 */
export function recordHref(projectId: string, register: string, recordId: string): string {
  const found = RECORD_PAGES[register];
  if (!found) throw new Error(`No record page for the "${register}" register`);
  return `${sectionHref(projectId, found.section)}/${register}/${encodeURIComponent(recordId)}`;
}

// ---------------------------------------------------------------------------
// Gate items, and the screen that satisfies each one
// ---------------------------------------------------------------------------

/**
 * Where to go to satisfy a gate item.
 *
 * `05`'s gate items are the honest answer to "what does this stage still want
 * from me" — and until now they were reported as a list of sentences with no
 * way through to the thing that would fix them. Every key in
 * `apps/api/src/projects/stages.ts` is here, and there is a test that says so:
 * an item with no screen is a sentence telling somebody to do something the
 * product gives them no way to do, which is the same failure as an empty state
 * naming an action its reader cannot take.
 */
export interface GateTarget {
  readonly section: SectionKey;
  /** The register inside that section, for the anchor and the link's words. */
  readonly register: string;
  /** What the reader would actually do there. Reads after "Go to". */
  readonly action: string;
  /**
   * What it COSTS while it stays open — the brief's "cause and consequence".
   *
   * This is copy, not data, so it lives here rather than on the API: it is the
   * same sentence for every project and the server would only be posting it
   * back. Keeping it beside the link means the coverage test in
   * `gate-targets.unit.test.ts` guards it too — a new gate item cannot arrive
   * with a link and no reason to care.
   *
   * Each one says a CONSEQUENCE, not a restatement of the label. "No drawing
   * past its promised date" already says what is wrong; what a reader at 6:45am
   * needs is why it should be today's problem rather than Thursday's.
   */
  readonly consequence: string;
  /**
   * What the evidence date means, when the item has one.
   *
   * `deadline` — a date something was owed by, so past is OVERDUE.
   * `since` — the date something started, so past is an AGE.
   *
   * One item is `since` and it is the reason this field exists: a blocker has no
   * due date, it has an age, and rendering "6 days overdue" against the day it
   * was raised would be wrong in a way that reads perfectly plausibly.
   */
  readonly dateMeans?: 'deadline' | 'since';
  /**
   * What the evidence COUNTS, singular and plural, so a row reads "3 drawings"
   * rather than "3". Required on every item the API attaches a count to, and
   * `gate-targets.unit.test.ts` checks that against `GATE_EVIDENCE_RESOURCE`
   * in both directions — a count with no noun would render as a bare figure and
   * an unused noun would be copy nobody sees.
   *
   * A pair rather than a singular plus an "s", because one of them is
   * "activity"/"activities" and the rule that handles it handles nothing else.
   */
  readonly noun?: readonly [string, string];
}

export const GATE_TARGETS: Readonly<Record<string, GateTarget>> = {
  // Award — four of these are the project's own details, which had no edit
  // screen at all until the redesign. The gate asked for something the product
  // could not do.
  client: {
    section: 'overview',
    register: 'details',
    action: 'the project details',
    consequence: 'Nothing can be invoiced against a project with no client on it.',
  },
  contract_value: {
    section: 'overview',
    register: 'details',
    action: 'the project details',
    consequence: 'Cost, commitment and every variation have nothing to be measured against.',
  },
  start_date: {
    section: 'overview',
    register: 'details',
    action: 'the project details',
    consequence: 'Diary coverage and every date-based check are counted from it.',
  },
  target_date: {
    section: 'overview',
    register: 'details',
    action: 'the project details',
    consequence: 'Nothing on the job can be judged late until the finish date exists.',
  },
  pm_assigned: {
    section: 'overview',
    register: 'team',
    action: 'the team',
    consequence: 'Nobody holds the job, so nothing on this list has an owner.',
  },
  milestone: {
    section: 'overview',
    register: 'milestones',
    action: 'the milestones',
    consequence: 'With no baseline date there is nothing for a slip to show up against.',
  },
  budget: {
    section: 'commercial',
    register: 'budget',
    action: 'the budget',
    consequence: 'The cost report cannot say whether this job is over or under.',
  },

  // Design
  deliverables_issued: {
    section: 'design',
    register: 'drawings',
    action: 'the drawings register',
    consequence: 'Nothing can be submitted or ordered from a drawing that is not issued.',
    noun: ['drawing', 'drawings'],
  },
  no_overdue: {
    section: 'design',
    register: 'drawings',
    action: 'the drawings register',
    consequence: 'Every day late here pushes the approval and the order behind it.',
    noun: ['drawing', 'drawings'],
  },

  // Approvals. The second one is satisfied EITHER by the consultant approving
  // the submittal or by somebody recording an exception on the material, and
  // the material register is where both are visible.
  no_late_submittals: {
    section: 'design',
    register: 'submittals',
    action: 'the submittals',
    consequence: 'A material waiting on one of these is stopped, not slowed.',
    noun: ['submittal', 'submittals'],
  },
  materials_approved: {
    section: 'procurement',
    register: 'materials',
    action: 'the blocked materials',
    consequence: 'These cannot be ordered at all until the approval or an exception exists.',
    noun: ['material', 'materials'],
  },

  // Procurement
  materials_at_site: {
    section: 'procurement',
    register: 'materials',
    action: 'the material register',
    consequence: 'Work planned on them cannot start.',
    noun: ['material', 'materials'],
  },
  no_late_eta: {
    section: 'procurement',
    register: 'materials',
    action: 'the material register',
    consequence: 'The programme already assumes these arrive before they are needed.',
    noun: ['material', 'materials'],
  },

  // Site. A blocker is raised on a TASK, which is where it is cleared too.
  activities_complete: {
    section: 'site',
    register: 'activities',
    action: 'the activities',
    consequence: 'Progress rolls up from these, so the percentage is understated until they close.',
    noun: ['activity', 'activities'],
  },
  no_blockers: {
    section: 'site',
    register: 'tasks',
    action: 'the tasks holding it up',
    consequence: 'Somebody is stopped and waiting on this right now.',
    // The one `since` item. Its date is when the blocker was raised, so past
    // means how long it has been open, not how late it is.
    dateMeans: 'since',
    noun: ['blocker', 'blockers'],
  },

  // Quality
  no_serious_ncr: {
    section: 'quality',
    register: 'quality',
    action: 'the NCRs',
    consequence: 'Non-conforming work stays where it is and gets built over.',
    noun: ['NCR', 'NCRs'],
  },
  inspections_closed: {
    section: 'quality',
    register: 'quality',
    action: 'the inspections',
    consequence: 'Work is carrying on over an area nobody has signed off.',
    noun: ['inspection', 'inspections'],
  },
  no_serious_snags: {
    section: 'quality',
    register: 'snags',
    action: 'the snag register',
    consequence: 'These are the ones the client finds on the handover walk.',
    noun: ['snag', 'snags'],
  },
  minor_resolved: {
    section: 'quality',
    register: 'snags',
    action: 'the snag register',
    consequence: 'Left to accumulate they become a list nobody can clear in the last week.',
    noun: ['snag', 'snags'],
  },

  // Handover
  checklist: {
    section: 'quality',
    register: 'handover',
    action: 'the handover checklist',
    consequence: 'The client cannot take the building without it.',
    noun: ['item', 'items'],
  },
  signoffs: {
    section: 'quality',
    register: 'handover',
    action: 'the sign-offs',
    consequence: 'Without signatures the handover is not evidenced if it is ever disputed.',
    noun: ['sign-off', 'sign-offs'],
  },
  dlp: {
    section: 'quality',
    register: 'handover',
    action: 'the handover details',
    consequence: 'The defects liability clock never starts, so it never ends.',
  },
};

/** The anchor id a register renders, so an in-page link can reach it. */
export function registerAnchor(register: string): string {
  return `register-${register}`;
}

/**
 * Where a gate item's "fix it" link should actually go.
 *
 * `sectionHref` alone was not enough, and the gap was invisible until somebody
 * pressed one: every Award gate item lives on the OVERVIEW, so its link
 * resolved to the page the reader was already standing on and **clicking it did
 * nothing at all**. The panel that exists to say "here is what to do next" had
 * a next step you could not reach.
 *
 * The two halves of the fix were both already built and were simply never
 * joined: `GATE_TARGETS` has carried a `register` for every item since it was
 * written, and `ProjectDetail` has rendered `registerAnchor(key)` as an `id` on
 * every one of those blocks. All that was missing is the `#`.
 *
 * `AppLink` does the scrolling — see the note there about why `preventDefault`
 * means the browser will not do it for us.
 */
export function gateHref(projectId: string, target: GateTarget): string {
  const base = sectionHref(projectId, target.section);
  return target.register ? `${base}#${registerAnchor(target.register)}` : base;
}
